import BaseService from "@/services/BaseService.ts";
import {supabase} from "@supabaseClient";
import {BankAccount} from "@/integrations/supabase/account.models.ts";
import bankService from "@bankService";

/**
 * Bank Account Service manages bank account operations
 */
export class BankAccountService extends BaseService {

    /**
     * Retrieves a bank account given its ID
     * @param {string} id Account ID to retrieve
     * @returns {BankAccount} retrieved bank account
     */
    async fetchBankAccountById(id: string) : Promise<BankAccount> {
        try {
            const { data, error } = await supabase
                .from('bank_accounts')
                .select()
                .eq('id', id)
                .single();

            if (error) {
                throw error
            }
            return data
        } catch (e) {
            await this.handleError(e);
        }
    }

    async fetchAllBankAccountsByUser(userId: string) : Promise<BankAccount[]> {
        const { data, error } = await supabase
            .from('bank_accounts')
            .select()
            .eq('user_id', userId);

        if (error) {
            await this.handleError(error);
            throw error
        }
        return data
    }

    /**
     * Creates a new bank account
     * @param request
     */
    async createBankAccount(request: BankAccountDetailsCreateRequest) : Promise<BankAccount> {
        try {
            const {
                userId,
                bankDetails,
                details,
            } = request;

            const {
                bankId: inputBankId,
                name: bankName,
                address: bankAddress
            } = bankDetails

            let bankId = inputBankId;

            // Step 1: If no bankId, insert a new bank
            if (!bankId) {
                const createdBank = await bankService.createBank({
                    name: bankName,
                    address: bankAddress
                });
                bankId = createdBank.id;
            } else if (bankAddress !== undefined) {
                // Step 2: If bankId present and bankAddress provided, best-effort update
                const existingBank = await bankService.fetchBankById(bankId);

                const storedAddress = existingBank.address;
                if (bankAddress !== storedAddress) {
                    await bankService.updateBankAddress(bankId, bankAddress);
                }
            }

            // Step 3: Insert into bank accounts

            const bankAccountPayload = this.convertRequestToSnakeCase({
                ...details,
                userId: userId,
                bankId: bankId,
            });

            const { data: newBankAccount, error: bankAccountError } = await supabase
                .from('bank_accounts')
                .insert(bankAccountPayload)
                .select()
                .single();

            if (bankAccountError) {
                throw bankAccountError;
            }

            return newBankAccount
        } catch (e) {
            await this.handleError(e);
            throw e
        }
    }
}

export default new BankAccountService();
